/**
 * WorkBuddy Cloud SDK 小程序适配层公开入口。
 *
 * 推荐用法（应用侧）—— 一站式工厂，把 polyfill、wx fetch、wx storage 与根客户端
 * 按固定顺序原子装配：
 *
 * ```ts
 * import { createMiniProgramWorkBuddyCloud } from '@genie/workbuddy-cloud-sdk/miniprogram';
 *
 * const cloud = createMiniProgramWorkBuddyCloud({
 *     endpoint: 'https://app.workbuddy.link', // 小程序没有 location.origin，必须显式传
 *     publishableKey: 'wbpk_xxx',
 * });
 * ```
 *
 * 三个适配件（`ensureMiniProgramPolyfills` / `createMiniProgramFetch` /
 * `createMiniProgramStorage`）也继续单独导出，供确有精细控制需要的场景手动组合
 * （顺序约束见各文件顶部注释）；一般接入直接用上面的工厂。
 *
 * 见 `platform/miniprogram/fetch.ts`、`storage.ts`、`polyfills.ts` 顶部注释
 * 里标注的占位/待验证事项——这一层还没有真机联调过。
 */

import { createWorkBuddyCloud } from '../../client.js';
import type { WorkBuddyCloudClient } from '../../client.js';
import { createMiniProgramFetch } from './fetch.js';
import { ensureMiniProgramPolyfills } from './polyfills.js';
import { createMiniProgramStorage } from './storage.js';
import type { MiniProgramWxLike } from './wx-types.js';

export { createMiniProgramFetch } from './fetch.js';
export { createMiniProgramStorage } from './storage.js';
export {
    ensureMiniProgramPolyfills,
    isStreamingSupported,
    MiniProgramHeaders,
    MiniProgramReadableStream,
} from './polyfills.js';
export type {
    MiniProgramWxLike,
    WxChunkReceivedResult,
    WxRequestFailResult,
    WxRequestOptions,
    WxRequestSuccessResult,
    WxRequestTask,
} from './wx-types.js';

/**
 * `createMiniProgramWorkBuddyCloud` 的入参。
 *
 * 只收 `publicConfig` 那三项公开配置 —— 刻意**不**透传根 `WorkBuddyCloudOptions`
 * 的 `fetch` / `storage` 注入口：那两个口子是给「非浏览器但也非小程序」的运行时
 * 组合用的；在小程序里放开它们，接入方就能绕过 wx 适配器塞进一个浏览器实现，
 * 错误要等到真机上才暴露。
 */
export interface MiniProgramWorkBuddyCloudOptions {
    /**
     * 数据面基址，来自 `publicConfig.endpoint`。
     *
     * 小程序宿主没有 `location.origin`，根 SDK 的同源回落在这里不存在——省略
     * 会在初始化时直接抛 `WorkBuddyCloudConfigError`。保持可选只为与根
     * `WorkBuddyCloudOptions` 的类型面对齐，实际接入**永远要传**。
     */
    endpoint?: string;
    /**
     * 环境中心 OAuth Relay 基址，来自 `publicConfig.oauthRelayBaseUrl`。
     *
     * 小程序登录走 `signInWithWechat`（`wx.login` + 第三方平台 code），不经过
     * Relay，所以这里可以不传。Relay 只有网页扫码登录才用得上。
     */
    oauthRelayBaseUrl?: string;
    /** 应用标识 key（`wbpk_` 前缀），来自 `publicConfig.publishableKey`。 */
    publishableKey: string;
    /**
     * 宿主 `wx` 对象，缺省取全局 `wx`。
     *
     * 应用代码不需要传；留这个口子是为了单测注入 mock，以及宿主把 `wx` 挂在
     * 非全局位置时显式传入。fetch 与 storage 适配器**共用同一个实例**。
     */
    wx?: MiniProgramWxLike;
}

/**
 * 创建小程序版 WorkBuddy Cloud 客户端（小程序接入的推荐入口）。
 *
 * 装配顺序固定，任何一步都不能省：
 *
 *   1. `ensureMiniProgramPolyfills()` —— 根 SDK 的共享出口在请求期直接
 *      `new Headers()`，小程序基础库缺这个全局对象时必须先补上；
 *   2. `createMiniProgramFetch(wx)` + `createMiniProgramStorage(wx)` ——
 *      网络与凭据存储绑定同一个 wx 实例；
 *   3. `createWorkBuddyCloud(...)` —— 返回的 client 与 Web 端同构，
 *      `cloud.auth / database / storage / llm` 用法完全一致。
 *
 * 把这个顺序收进 SDK 是刻意的：三个适配件手动组合时，漏掉 polyfill 或调换顺序，
 * 得到的都是一个「构造能过、第一个请求才炸」的 client，真机上极难归因。
 *
 * 依赖方向说明：这里从 `../../client.js`（而非根 barrel `../../index.js`）取
 * `createWorkBuddyCloud`，返回类型只用 `import type` 引入 —— 保证小程序出口对
 * 核心 SDK 的依赖严格单向（根入口永远不反向 import 本模块），`./miniprogram`
 * 的 ESM/CJS 出口也就不会与根入口形成运行时环。
 *
 * @throws 没有可用 wx 对象、或三项公开配置不合法（复用根 SDK 的
 * `WorkBuddyCloudConfigError`）时立即抛出。
 *
 * ⚠️ `cloud.storage` 的文件下载在小程序运行时**不可用**：本适配层的
 * `fetch` 不支持 `Response.blob()/arrayBuffer()`（小程序没有全局 `Blob`，
 * 详见 `fetch.ts` 里 `buildResponse` 的注释），调用会拿到一条明确报错的
 * rejected promise，而不是静默失败或返回错位的数据；`cloud.auth/database/llm`
 * 不受影响。
 */
export function createMiniProgramWorkBuddyCloud<Database = unknown>(
    options: MiniProgramWorkBuddyCloudOptions,
): WorkBuddyCloudClient<Database> {
    const wxInstance = options.wx ?? (globalThis as { wx?: MiniProgramWxLike }).wx;
    if (!wxInstance) {
        throw new Error(
            'createMiniProgramWorkBuddyCloud: 未找到全局 wx 对象，请在小程序环境调用，或显式传入 options.wx。',
        );
    }

    ensureMiniProgramPolyfills();

    return createWorkBuddyCloud<Database>({
        endpoint: options.endpoint,
        oauthRelayBaseUrl: options.oauthRelayBaseUrl,
        publishableKey: options.publishableKey,
        fetch: createMiniProgramFetch(wxInstance),
        storage: createMiniProgramStorage(wxInstance),
    });
}
